import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, Mail, Phone } from "lucide-react";
import { VISIT_LABELS } from "@/components/VisitReportForm";
import { INQUIRY_STATUS } from "@/components/InquiriesPanel";

const fmt = (k, v) => (/(revenue|ebitda|net_result|monthly_rent|debts|stock_value|seller_valuation|proac_estimate)$/.test(k) && v !== "" && !Number.isNaN(Number(v))
  ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v) : String(v));

const Report = ({ r }) => {
  const [open, setOpen] = useState(false);
  const d = r.data || {};
  const entries = Object.entries(VISIT_LABELS).filter(([k]) => d[k] !== undefined && d[k] !== "");
  return (
    <div data-testid={`visit-report-${r.report_id}`} className="bg-white border border-gray-200">
      <button onClick={() => setOpen(!open)} data-testid={`visit-report-toggle-${r.report_id}`} className="w-full p-4 flex flex-wrap items-center justify-between gap-3 text-left">
        <div>
          <div className="font-medium text-[#1B2A4A]">{d.company_name || "Entreprise"} · {r.seller_name}</div>
          <div className="text-xs text-gray-500 mt-0.5">{[d.sector, d.department].filter(Boolean).join(" · ")} · visite du {new Date(r.created_at).toLocaleDateString("fr-FR")} par {r.created_by}</div>
        </div>
        <div className="flex items-center gap-3 text-sm">
          {d.ebitda && <span className="text-[#1B2A4A]">EBITDA {fmt("ebitda", d.ebitda)}</span>}
          {d.potential && <span className="px-2 py-0.5 bg-[#E0C173]/30 text-[#8a6d22] text-xs">Potentiel {d.potential}/5</span>}
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <dl className="border-t border-gray-100 p-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm">
          {entries.map(([k, l]) => <div key={k} className={["competitors", "main_clients", "suppliers", "strengths", "weaknesses", "notes"].includes(k) ? "sm:col-span-2 lg:col-span-3" : ""}><dt className="text-xs text-gray-500">{l}</dt><dd className="text-[#1B2A4A] whitespace-pre-line">{fmt(k, d[k])}</dd></div>)}
        </dl>
      )}
    </div>
  );
};

export const VisitReportsPanel = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/visit-reports").then((r) => setItems(r.data)).catch(() => {}); }, []);
  if (!items.length) return <p data-testid="visit-reports-empty" className="text-gray-500 py-10 text-center">Aucune fiche de visite. Utilisez le « Mode RDV » pendant vos rendez-vous.</p>;
  return <div className="space-y-3" data-testid="visit-reports">{items.map((r) => <Report key={r.report_id} r={r} />)}</div>;
};

const KIND = { photos: "Photos", video: "Vidéo", both: "Photos + vidéo" };

export const MediaRequestsPanel = ({ onChange }) => {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/media-requests").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/media-requests/${id}/status`, { status }); toast.success("Statut mis à jour"); load(); onChange?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!items.length) return <p data-testid="media-requests-empty" className="text-gray-500 py-10 text-center">Aucune demande de photos ou de vidéo.</p>;
  return (
    <div className="space-y-3" data-testid="media-requests">
      {items.map((m) => (
        <div key={m.request_id} data-testid={`media-req-${m.request_id}`} className={`bg-white border p-4 ${m.status === "new" ? "border-l-4 border-l-[#E0C173] border-gray-200" : "border-gray-200"}`}>
          <div className="flex flex-wrap justify-between gap-2">
            <div className="font-medium text-[#1B2A4A] flex flex-wrap items-center gap-x-3 gap-y-1">{m.seller_name}<span className="text-[11px] uppercase tracking-wider bg-[#1B2A4A] text-white px-2 py-0.5">{KIND[m.kind]}</span>
              <a href={`mailto:${m.seller_email}`} className="text-sm font-normal text-[#475569] inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{m.seller_email}</a>
              {m.seller_phone && <a href={`tel:${m.seller_phone}`} className="text-sm font-normal text-[#475569] inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{m.seller_phone}</a>}
            </div>
            <div className="text-xs text-gray-500">{new Date(m.created_at).toLocaleString("fr-FR")}</div>
          </div>
          {m.business_title && <div className="text-xs text-[#C9A85A] uppercase tracking-wider mt-1">{m.business_title}</div>}
          {m.message && <p className="mt-2 text-sm text-[#475569]">{m.message}</p>}
          <div className="mt-3 flex flex-wrap gap-2">
            {Object.entries(INQUIRY_STATUS).map(([k, s]) => <button key={k} data-testid={`media-status-${k}-${m.request_id}`} onClick={() => m.status !== k && setStatus(m.request_id, k)} className={`text-xs px-3 py-1 border ${m.status === k ? s.cls + " font-semibold" : "border-gray-300 text-gray-600"}`}>{s.label}</button>)}
          </div>
        </div>
      ))}
    </div>
  );
};
